/**
 * Files Page
 * File library management with folder organization
 * Follows the reference UI pattern with left sidebar navigation
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import {
    Plus,
    Search,
    Trash2,
    ChevronLeft,
    ChevronRight,
    RefreshCw,
    Loader2,
    Filter as FilterIcon,
    Upload,
    FolderPlus,
    Folder,
    FileIcon,
    Image,
    File,
    Film,
    Music,
    FileText,
    Archive,
    FolderOpen,
    MoveRight,
    LayoutGrid,
    Info,
    Download,
} from 'lucide-react';
import { FilterBuilder, type Filter } from '@/components/filters';
import { useRightSidebar, type SidebarSection } from '@/components/sidebar/index';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { api, type ApiListResponse, type ApiResponse } from '@/lib/api';
import type { Field } from '@/hooks/useFields';
import { useAuthStore } from '@/stores/auth';
import { FilesNavigation } from './components/FilesNavigation';
import { LayoutWrapper, LayoutOptionsWrapper } from '@/layouts/LayoutWrapper';
import { getAvailableLayouts, type LayoutId } from '@/layouts';
import { usePreset } from '@/hooks/usePreset';
import { formatFileSize } from '@/utils/format';

// API URL for asset URLs
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000';

// File type from API
interface FileItem {
    id: string;
    storage: string;
    filename_disk: string | null;
    filename_download: string;
    title: string | null;
    type: string | null;
    folder: string | null;
    uploaded_by: string | null;
    uploaded_on: string | null;
    modified_by: string | null;
    modified_on: string | null;
    filesize: number | null;
    width: number | null;
    height: number | null;
    description: string | null;
}

interface FolderItem {
    id: string;
    name: string;
    parent: string | null;
}

// Get file icon based on MIME type
function getFileIcon(type: string | null, className = "h-5 w-5") {
    if (!type) return <FileIcon className={className} />;
    if (type.startsWith('image/')) return <Image className={className} />;
    if (type.startsWith('video/')) return <Film className={className} />;
    if (type.startsWith('audio/')) return <Music className={className} />;
    if (type.includes('pdf') || type.includes('document') || type.includes('text')) return <FileText className={className} />;
    if (type.includes('zip') || type.includes('tar') || type.includes('compress')) return <Archive className={className} />;
    return <File className={className} />;
}

// Get file thumbnail URL
function getFileThumbnail(file: FileItem, size = 200): string | null {
    if (!file.type?.startsWith('image/')) return null;
    return `${API_URL}/assets/${file.id}?width=${size}&height=${size}&fit=cover`;
}

// Filter fields for the file library
const filterFields: Field[] = [
    {
        field: 'filename_download',
        type: 'string',
        collection: 'cms_files',
        schema: null,
        meta: null,
    },
    {
        field: 'title',
        type: 'string',
        collection: 'cms_files',
        schema: null,
        meta: null,
    },
    {
        field: 'type',
        type: 'string',
        collection: 'cms_files',
        schema: null,
        meta: null,
    },
    {
        field: 'filesize',
        type: 'integer',
        collection: 'cms_files',
        schema: null,
        meta: null,
    },
    {
        field: 'uploaded_on',
        type: 'datetime',
        collection: 'cms_files',
        schema: null,
        meta: null,
    },
];

type SpecialView = 'all' | 'mine' | 'recent';

export function FilesPage() {
    const { folderId } = useParams<{ folderId?: string }>();
    const navigate = useNavigate();
    const location = useLocation();

    // Determine special view from pathname
    const special: SpecialView | undefined = useMemo(() => {
        const path = location.pathname;
        if (path.endsWith('/all')) return 'all';
        if (path.endsWith('/mine')) return 'mine';
        if (path.endsWith('/recent')) return 'recent';
        return undefined;
    }, [location.pathname]);

    // Auth store for token
    const { accessToken, user } = useAuthStore();

    // Use preset for layout persistence
    const {
        layout,
        setLayout,
        layoutQuery,
        setLayoutQuery,
        layoutOptions,
        allLayoutOptions,
        setLayoutOptions,
    } = usePreset({
        collection: 'cms_files',
        temporary: false, // Save to localStorage like content and users
    });

    // Local state
    const [searchInput, setSearchInput] = useState('');
    const [files, setFiles] = useState<FileItem[]>([]);
    const [folders, setFolders] = useState<FolderItem[]>([]);
    const [allFolders, setAllFolders] = useState<FolderItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
    const [page, setPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [limit, setLimit] = useState(50);
    const [foldersRefreshTrigger, setFoldersRefreshTrigger] = useState(0);

    // Dialog states
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [createFolderDialogOpen, setCreateFolderDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [moveDialogOpen, setMoveDialogOpen] = useState(false);
    const [newFolderName, setNewFolderName] = useState('');
    const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);
    const [filter, setFilter] = useState<Filter | null>(null);
    const [moveTargetFolder, setMoveTargetFolder] = useState<string | null>(null);

    // Upload state
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadFiles, setUploadFiles] = useState<File[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Current folder info
    const [breadcrumbs, setBreadcrumbs] = useState<FolderItem[]>([]);
    const [currentFolder, setCurrentFolder] = useState<FolderItem | null>(null);

    // Determine page title based on route
    const pageTitle = useMemo(() => {
        if (special === 'all') return 'All Files';
        if (special === 'mine') return 'My Files';
        if (special === 'recent') return 'Recent Files';
        if (currentFolder) return currentFolder.name;
        return 'File Library';
    }, [special, currentFolder]);

    // Fetch all folders for move dialog
    const fetchAllFolders = useCallback(async () => {
        try {
            const response = await api.get<ApiListResponse<FolderItem>>('/folders?limit=-1&sort=name');
            setAllFolders(response.data || []);
        } catch (error) {
            console.error('Error fetching all folders:', error);
        }
    }, []);

    // Fetch files and folders
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            // Build filter array based on route (always include type filter to exclude folders)
            const filterArray: Record<string, unknown>[] = [
                { type: { _nnull: true } }, // Exclude folders (folders have null type)
            ];

            if (special === 'all') {
                // All files - already filtered by type
            } else if (special === 'mine') {
                // My files - filter by uploaded_by
                if (user?.id) {
                    filterArray.push({ uploaded_by: { _eq: user.id } });
                }
            } else if (special === 'recent') {
                // Recent files - files uploaded in last 7 days
                const sevenDaysAgo = new Date();
                sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
                filterArray.push({ uploaded_on: { _gt: sevenDaysAgo.toISOString() } });
            } else if (folderId) {
                // Specific folder
                filterArray.push({ folder: { _eq: folderId } });
            } else {
                // Root folder
                filterArray.push({ folder: { _null: true } });
            }

            // Add user filter if exists
            if (filter && Object.keys(filter).length > 0) {
                filterArray.push(filter);
            }

            // Build query params with field selection like reference
            const queryParams = new URLSearchParams({
                limit: limit.toString(),
                page: page.toString(),
                meta: 'total_count,filter_count',
            });

            // Add fields[] array format like reference
            const fields = ['id', 'modified_on', 'type', 'title', 'filesize', 'filename_download', 'folder', 'width', 'height', 'uploaded_on'];
            fields.forEach(field => {
                queryParams.append('fields[]', field);
            });

            // Add combined filter with _and
            queryParams.set('filter', JSON.stringify({ _and: filterArray }));

            // Add sort array format like reference
            if (special === 'recent') {
                queryParams.append('sort[]', '-modified_on');
            } else {
                queryParams.append('sort[]', '-uploaded_on');
            }

            if (searchInput) {
                queryParams.set('search', searchInput);
            }

            // Fetch files
            const filesResponse = await api.get<ApiListResponse<FileItem>>(`/files?${queryParams}`);
            setFiles(filesResponse.data || []);
            setTotalCount(filesResponse.meta?.filter_count || 0);

            // Fetch folders in current directory (only for folder views, not special views)
            if (!special) {
                const folderQueryParams = new URLSearchParams({
                    filter: JSON.stringify(folderId ? { parent: { _eq: folderId } } : { parent: { _null: true } }),
                    sort: 'name',
                });
                const foldersResponse = await api.get<ApiListResponse<FolderItem>>(`/folders?${folderQueryParams}`);
                setFolders(foldersResponse.data || []);
            } else {
                setFolders([]);
            }

            // Fetch breadcrumbs if in a folder
            if (folderId && !special) {
                await fetchBreadcrumbs(folderId);
            } else {
                setBreadcrumbs([]);
                setCurrentFolder(null);
            }
        } catch (error) {
            console.error('Error fetching files:', error);
            toast.error('Failed to load files');
        } finally {
            setLoading(false);
        }
    }, [folderId, special, filter, searchInput, page, limit, user?.id]);

    // Fetch breadcrumb trail
    const fetchBreadcrumbs = async (currentFolderId: string): Promise<void> => {
        const crumbs: FolderItem[] = [];
        let currentId: string | null = currentFolderId;

        while (currentId) {
            try {
                const folderResponse = await api.get<ApiResponse<FolderItem>>(`/folders/${currentId}`);
                const folderData: FolderItem = folderResponse.data;
                crumbs.unshift(folderData);
                currentId = folderData.parent;
            } catch {
                break;
            }
        }

        setBreadcrumbs(crumbs);
        const lastCrumb = crumbs.length > 0 ? crumbs[crumbs.length - 1] : null;
        setCurrentFolder(lastCrumb ?? null);
    };

    // Load data on mount and when dependencies change
    useEffect(() => {
        fetchData();
        fetchAllFolders();
    }, [fetchData, fetchAllFolders]);

    // Debounced search
    useEffect(() => {
        const timer = setTimeout(() => {
            setPage(1);
        }, 300);

        return () => clearTimeout(timer);
    }, [searchInput]);

    // Handle file upload using FormData with fetch
    const handleUpload = async () => {
        const filesToUpload = uploadFiles;
        if (filesToUpload.length === 0) return;

        setUploading(true);
        setUploadProgress(0);

        try {
            for (let i = 0; i < filesToUpload.length; i++) {
                const file = filesToUpload[i];
                if (!file) continue;

                const formData = new FormData();
                formData.append('file', file);
                if (folderId) {
                    formData.append('folder', folderId);
                }

                const response = await fetch(`${API_URL}/files`, {
                    method: 'POST',
                    headers: {
                        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
                    },
                    body: formData,
                });

                if (!response.ok) {
                    throw new Error(`Upload failed: ${response.statusText}`);
                }

                setUploadProgress(((i + 1) / filesToUpload.length) * 100);
            }

            toast.success(`Successfully uploaded ${filesToUpload.length} file(s)`);
            setUploadDialogOpen(false);
            setUploadFiles([]);
            fetchData();
        } catch (error) {
            console.error('Upload error:', error);
            toast.error('Failed to upload files');
        } finally {
            setUploading(false);
            setUploadProgress(0);
        }
    };

    // Handle folder creation
    const handleCreateFolder = async () => {
        if (!newFolderName.trim()) return;

        try {
            await api.post('/folders', {
                name: newFolderName.trim(),
                parent: folderId || null,
            });

            toast.success('Folder created');
            setCreateFolderDialogOpen(false);
            setNewFolderName('');
            fetchData();
            fetchAllFolders();
            // Trigger folders refresh in navigation
            setFoldersRefreshTrigger(Date.now());
        } catch (error) {
            console.error('Error creating folder:', error);
            toast.error('Failed to create folder');
        }
    };

    // Handle delete
    const handleDelete = async () => {
        const selectedFiles = Array.from(selectedItems).filter(id =>
            files.some(f => f.id === id)
        );
        const selectedFolders = Array.from(selectedItems).filter(id =>
            folders.some(f => f.id === id)
        );

        try {
            // Delete files
            if (selectedFiles.length > 0) {
                await api.delete('/files', { keys: selectedFiles });
            }

            // Delete folders
            for (const id of selectedFolders) {
                await api.delete(`/folders/${id}`);
            }

            toast.success('Items deleted');
            setSelectedItems(new Set());
            setDeleteDialogOpen(false);
            fetchData();
            fetchAllFolders();
            // Trigger folders refresh in navigation if folders were deleted
            if (selectedFolders.length > 0) {
                setFoldersRefreshTrigger(Date.now());
            }
        } catch (error: any) {
            console.error('Error deleting items:', error);
            // Extract error message from ApiError or use default
            const errorMessage = error?.message || 'Failed to delete items';
            toast.error(errorMessage);
        }
    };

    // Handle move files
    const handleMove = async () => {
        const selectedFiles = Array.from(selectedItems).filter(id =>
            files.some(f => f.id === id)
        );

        if (selectedFiles.length === 0) return;

        try {
            for (const fileId of selectedFiles) {
                await api.patch(`/files/${fileId}`, {
                    folder: moveTargetFolder,
                });
            }

            toast.success(`Moved ${selectedFiles.length} file(s)`);
            setSelectedItems(new Set());
            setMoveDialogOpen(false);
            setMoveTargetFolder(null);
            fetchData();
        } catch (error) {
            console.error('Error moving files:', error);
            toast.error('Failed to move files');
        }
    };

    // Handle select all
    const handleSelectAll = () => {
        if (selectedItems.size === files.length + folders.length) {
            setSelectedItems(new Set());
        } else {
            const allIds = [
                ...folders.map(f => f.id),
                ...files.map(f => f.id),
            ];
            setSelectedItems(new Set(allIds));
        }
    };

    // Handle drag and drop
    const handleDrop = useCallback(async (e: React.DragEvent) => {
        e.preventDefault();

        const droppedFiles = Array.from(e.dataTransfer.files);
        if (droppedFiles.length === 0) return;

        setUploadFiles(droppedFiles);
        setUploadDialogOpen(true);
    }, []);

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
    }, []);

    // File input change
    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        if (files.length > 0) {
            setUploadFiles(files);
            setUploadDialogOpen(true);
        }
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // Navigate to folder
    const navigateToFolder = (targetFolderId: string | null) => {
        if (targetFolderId) {
            navigate(`/admin/files/folders/${targetFolderId}`);
        } else {
            navigate('/admin/files');
        }
    };

    // Calculate pagination
    const totalPages = Math.ceil(totalCount / limit);

    // Calculate active filter count
    const activeFilterCount = useMemo(() => {
        if (!filter) return 0;
        const countFilters = (f: Filter): number => {
            if (!f || typeof f !== 'object') return 0;
            const keys = Object.keys(f);
            if (keys.length === 0) return 0;
            if (keys[0] === '_and' || keys[0] === '_or') {
                const arr = f[keys[0] as keyof Filter];
                if (Array.isArray(arr)) {
                    return arr.reduce((sum, item) => sum + countFilters(item as Filter), 0);
                }
            }
            return 1;
        };
        return countFilters(filter);
    }, [filter]);

    // Current layout from preset (default to tabular)
    const currentLayout = (layout || 'tabular') as LayoutId;

    // Build sidebar sections
    const sidebarSections: SidebarSection[] = useMemo(() => {
        const selectedFile = files.find(f => selectedItems.has(f.id));

        const sections: SidebarSection[] = [
            {
                id: 'info',
                icon: <Info className="h-5 w-5" />,
                title: 'Information',
                defaultOpen: true,
                content: (
                    <div className="space-y-4 text-sm">
                        <div>
                            <span className="text-muted-foreground">Total Files</span>
                            <p className="font-medium">{totalCount}</p>
                        </div>
                        <div>
                            <span className="text-muted-foreground">Current View</span>
                            <p className="font-medium">{pageTitle}</p>
                        </div>
                    </div>
                ),
            },
            {
                id: `layout-${currentLayout}-files`,
                icon: <LayoutGrid className="h-5 w-5" />,
                title: 'Layout Options',
                content: (
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label className="text-xs text-muted-foreground">Layout</Label>
                            <Select value={currentLayout} onValueChange={(v) => setLayout(v)}>
                                <SelectTrigger className="w-full">
                                    <SelectValue placeholder="Select layout" />
                                </SelectTrigger>
                                <SelectContent>
                                    {getAvailableLayouts().map((layoutConfig) => (
                                        <SelectItem key={layoutConfig.id} value={layoutConfig.id}>
                                            {layoutConfig.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        {/* Layout-specific options */}
                        <LayoutOptionsWrapper
                            layout={currentLayout}
                            layoutOptions={allLayoutOptions as any || {}}
                            onLayoutOptionsChange={(opts) => setLayoutOptions((opts as any)?.[currentLayout] || null)}
                            collection="cms_files"
                            fields={filterFields}
                        />
                    </div>
                ),
            },
        ];

        // Add file details section when a file is selected
        if (selectedFile) {
            sections.unshift({
                id: 'details',
                icon: <FileIcon className="h-5 w-5" />,
                title: 'File Details',
                defaultOpen: true,
                content: (
                    <div className="space-y-4">
                        {/* Thumbnail */}
                        {selectedFile.type?.startsWith('image/') && (
                            <div className="aspect-video bg-muted rounded-lg overflow-hidden">
                                <img
                                    src={getFileThumbnail(selectedFile, 400) || ''}
                                    alt={selectedFile.title || selectedFile.filename_download}
                                    className="w-full h-full object-contain"
                                />
                            </div>
                        )}

                        {/* File info */}
                        <div className="space-y-3 text-sm">
                            <div>
                                <span className="text-muted-foreground">Filename</span>
                                <p className="font-medium break-all">{selectedFile.filename_download}</p>
                            </div>
                            {selectedFile.title && (
                                <div>
                                    <span className="text-muted-foreground">Title</span>
                                    <p className="font-medium">{selectedFile.title}</p>
                                </div>
                            )}
                            <div>
                                <span className="text-muted-foreground">Type</span>
                                <p className="font-medium">{selectedFile.type || 'Unknown'}</p>
                            </div>
                            <div>
                                <span className="text-muted-foreground">Size</span>
                                <p className="font-medium">{formatFileSize(selectedFile.filesize)}</p>
                            </div>
                            {selectedFile.width && selectedFile.height && (
                                <div>
                                    <span className="text-muted-foreground">Dimensions</span>
                                    <p className="font-medium">{selectedFile.width} × {selectedFile.height}</p>
                                </div>
                            )}
                            <div>
                                <span className="text-muted-foreground">Uploaded</span>
                                <p className="font-medium">
                                    {selectedFile.uploaded_on
                                        ? new Date(selectedFile.uploaded_on).toLocaleString()
                                        : 'Unknown'}
                                </p>
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="pt-2">
                            <Button
                                variant="outline"
                                size="sm"
                                className="w-full"
                                asChild
                            >
                                <a
                                    href={`${API_URL}/assets/${selectedFile.id}?download`}
                                    download={selectedFile.filename_download}
                                >
                                    <Download className="h-4 w-4 mr-2" />
                                    Download
                                </a>
                            </Button>
                        </div>
                    </div>
                ),
            });
        }

        return sections;
    }, [files, selectedItems, totalCount, pageTitle, currentLayout, layout, layoutOptions, setLayout, setLayoutOptions]);

    // Right sidebar hook
    useRightSidebar(sidebarSections);

    return (
        <div className="flex h-full">
            {/* Left Sidebar - Navigation */}
            <div className="w-60 border-r border-border shrink-0 overflow-hidden flex flex-col">
                <div className="flex-1 overflow-y-auto">
                    <FilesNavigation
                        currentFolder={folderId}
                        currentSpecial={special as SpecialView | undefined}
                        refreshTrigger={foldersRefreshTrigger}
                    />
                </div>
            </div>

            {/* Main Content */}
            <div
                className="flex-1 flex flex-col min-w-0 overflow-hidden"
                onDrop={handleDrop}
                onDragOver={handleDragOver}
            >
                {/* Header */}
                <div className="border-b border-border bg-background px-6 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            {/* Icon */}
                            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
                                {special ? (
                                    <FileIcon className="h-5 w-5 text-primary" />
                                ) : currentFolder ? (
                                    <FolderOpen className="h-5 w-5 text-primary" />
                                ) : (
                                    <Folder className="h-5 w-5 text-primary" />
                                )}
                            </div>
                            <div>
                                {/* Breadcrumbs */}
                                <div className="flex items-center gap-1 text-sm">
                                    {!special && (
                                        <>
                                            <button
                                                onClick={() => navigateToFolder(null)}
                                                className={cn(
                                                    'hover:text-foreground transition-colors',
                                                    !folderId ? 'text-foreground font-semibold' : 'text-muted-foreground'
                                                )}
                                            >
                                                File Library
                                            </button>
                                            {breadcrumbs.map((folder, index) => (
                                                <span key={folder.id} className="flex items-center gap-1">
                                                    <ChevronRight className="h-3 w-3 text-muted-foreground" />
                                                    <button
                                                        onClick={() => navigateToFolder(folder.id)}
                                                        className={cn(
                                                            'hover:text-foreground transition-colors',
                                                            index === breadcrumbs.length - 1
                                                                ? 'text-foreground font-semibold'
                                                                : 'text-muted-foreground'
                                                        )}
                                                    >
                                                        {folder.name}
                                                    </button>
                                                </span>
                                            ))}
                                        </>
                                    )}
                                    {special && (
                                        <span className="text-foreground font-semibold">{pageTitle}</span>
                                    )}
                                </div>
                                <p className="text-sm text-muted-foreground">
                                    {totalCount} {totalCount === 1 ? 'item' : 'items'}
                                </p>
                            </div>
                        </div>

                        {/* Header Actions */}
                        <div className="flex items-center gap-2">
                            {/* Search */}
                            <div className="relative flex items-center">
                                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                <Input
                                    type="text"
                                    placeholder="Search files..."
                                    value={searchInput}
                                    onChange={(e) => setSearchInput(e.target.value)}
                                    className="w-64 pl-9 pr-10"
                                />
                                {/* Filter button */}
                                <Popover open={filterPopoverOpen} onOpenChange={setFilterPopoverOpen}>
                                    <PopoverTrigger asChild>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className={cn(
                                                "absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7",
                                                activeFilterCount > 0 && "text-primary"
                                            )}
                                        >
                                            <FilterIcon className="h-4 w-4" />
                                            {activeFilterCount > 0 && (
                                                <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] text-primary-foreground">
                                                    {activeFilterCount}
                                                </span>
                                            )}
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-[500px]" align="end">
                                        <div className="space-y-4">
                                            <h4 className="font-medium">Filters</h4>
                                            <FilterBuilder
                                                value={filter}
                                                onChange={setFilter}
                                                fields={filterFields}
                                                collection="cms_files"
                                            />
                                        </div>
                                    </PopoverContent>
                                </Popover>
                            </div>

                            {/* Batch actions */}
                            {selectedItems.size > 0 && (
                                <>
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={() => setMoveDialogOpen(true)}
                                        title="Move selected files"
                                    >
                                        <MoveRight className="h-4 w-4" />
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={() => setDeleteDialogOpen(true)}
                                        className="text-destructive hover:text-destructive"
                                        title="Delete selected items"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </>
                            )}

                            {/* Add dropdown */}
                            {!special && (
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button>
                                            <Plus className="h-4 w-4" />
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                        <DropdownMenuItem onClick={() => fileInputRef.current?.click()}>
                                            <Upload className="h-4 w-4 mr-2" />
                                            Upload Files
                                        </DropdownMenuItem>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem onClick={() => setCreateFolderDialogOpen(true)}>
                                            <FolderPlus className="h-4 w-4 mr-2" />
                                            Create Folder
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            )}

                            {/* Hidden file input */}
                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                className="hidden"
                                onChange={handleFileInputChange}
                            />
                        </div>
                    </div>
                </div>

                {/* Secondary toolbar */}
                <div className="flex items-center justify-between border-b border-border bg-muted/30 px-6 py-2">
                    <div className="flex items-center gap-3 text-sm text-muted-foreground">
                        {selectedItems.size > 0 ? (
                            <>
                                <span>{selectedItems.size} selected</span>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setSelectedItems(new Set())}
                                >
                                    Clear
                                </Button>
                            </>
                        ) : (
                            <span>{folders.length} folders, {files.length} files</span>
                        )}
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => fetchData()}
                            disabled={loading}
                        >
                            <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
                        </Button>
                    </div>

                    <div className="flex items-center gap-2">
                        {/* Select all */}
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleSelectAll}
                        >
                            {selectedItems.size === files.length + folders.length && selectedItems.size > 0
                                ? 'Deselect All'
                                : 'Select All'}
                        </Button>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-auto">
                    {loading ? (
                        <div className="flex items-center justify-center h-full">
                            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                        </div>
                    ) : files.length === 0 && folders.length === 0 ? (
                        <div className="flex h-full flex-col items-center justify-center text-center p-6">
                            <div className="rounded-full bg-muted p-4">
                                <FileIcon className="h-8 w-8 text-muted-foreground" />
                            </div>
                            <h3 className="mt-4 text-lg font-semibold text-foreground">No files found</h3>
                            <p className="mt-1 text-sm text-muted-foreground">
                                {special
                                    ? 'No files match this filter'
                                    : 'Upload files or create a folder to get started'}
                            </p>
                            {!special && (
                                <Button className="mt-4" onClick={() => fileInputRef.current?.click()}>
                                    <Upload className="mr-2 h-4 w-4" />
                                    Upload Files
                                </Button>
                            )}
                        </div>
                    ) : (
                        <LayoutWrapper
                            layout={currentLayout}
                            collection="cms_files"
                            primaryKeyField="id"
                            items={files as any}
                            loading={loading}
                            error={null}
                            page={page}
                            limit={limit}
                            totalCount={totalCount}
                            itemCount={files.length}
                            totalPages={totalPages}
                            onPageChange={setPage}
                            onLimitChange={setLimit}
                            selection={Array.from(selectedItems)}
                            onSelectionChange={(ids) => {
                                setSelectedItems(new Set(ids as string[]));
                            }}
                            showSelect="multiple"
                            sort={layoutQuery?.sort || []}
                            onSortChange={(sort) => {
                                setLayoutQuery({
                                    ...layoutQuery,
                                    sort,
                                    page: layoutQuery?.page || 1,
                                    fields: layoutQuery?.fields,
                                });
                            }}
                            layoutOptions={layoutOptions as any || {}}
                            onLayoutOptionsChange={(opts) => setLayoutOptions(opts as any)}
                            layoutQuery={(layoutQuery as any) || {}}
                            onLayoutQueryChange={(q) => setLayoutQuery(q as any)}
                            fields={filterFields}
                            onItemClick={(item) => {
                                // Navigate to file detail page
                                navigate(`/admin/files/${item.id}`);
                            }}
                            onRefresh={() => fetchData()}
                            readonly={false}
                        />
                    )}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-between border-t border-border bg-background px-6 py-3">
                        <div className="text-sm text-muted-foreground">
                            {totalCount !== undefined && (
                                <>
                                    Showing {((page - 1) * limit) + 1} to{' '}
                                    {Math.min(page * limit, totalCount)} of{' '}
                                    {totalCount} items
                                </>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline"
                                size="icon"
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                disabled={page <= 1}
                            >
                                <ChevronLeft className="h-4 w-4" />
                            </Button>
                            <span className="text-sm text-muted-foreground">
                                Page {page} of {totalPages}
                            </span>
                            <Button
                                variant="outline"
                                size="icon"
                                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                disabled={page >= totalPages}
                            >
                                <ChevronRight className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                )}
            </div>


            {/* Upload Dialog */}
            <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Upload Files</DialogTitle>
                        <DialogDescription>
                            {uploadFiles.length} file(s) selected for upload
                            {currentFolder && ` to "${currentFolder.name}"`}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4">
                        <div className="max-h-48 overflow-auto space-y-2">
                            {uploadFiles.map((file, index) => (
                                <div key={index} className="flex items-center gap-2 text-sm">
                                    {getFileIcon(file.type)}
                                    <span className="truncate flex-1">{file.name}</span>
                                    <span className="text-muted-foreground">{formatFileSize(file.size)}</span>
                                </div>
                            ))}
                        </div>

                        {uploading && (
                            <div className="space-y-2">
                                <Progress value={uploadProgress} />
                                <p className="text-sm text-muted-foreground text-center">
                                    Uploading... {Math.round(uploadProgress)}%
                                </p>
                            </div>
                        )}
                    </div>

                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setUploadDialogOpen(false);
                                setUploadFiles([]);
                            }}
                            disabled={uploading}
                        >
                            Cancel
                        </Button>
                        <Button onClick={handleUpload} disabled={uploading}>
                            {uploading ? (
                                <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Uploading...
                                </>
                            ) : (
                                'Upload'
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Create Folder Dialog */}
            <Dialog open={createFolderDialogOpen} onOpenChange={setCreateFolderDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Create Folder</DialogTitle>
                        <DialogDescription>
                            Create a new folder{currentFolder ? ` in "${currentFolder.name}"` : ''}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="folderName">Folder Name</Label>
                            <Input
                                id="folderName"
                                value={newFolderName}
                                onChange={(e) => setNewFolderName(e.target.value)}
                                placeholder="New Folder"
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        handleCreateFolder();
                                    }
                                }}
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setCreateFolderDialogOpen(false);
                                setNewFolderName('');
                            }}
                        >
                            Cancel
                        </Button>
                        <Button onClick={handleCreateFolder} disabled={!newFolderName.trim()}>
                            Create
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Move Files Dialog */}
            <Dialog open={moveDialogOpen} onOpenChange={setMoveDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Move Files</DialogTitle>
                        <DialogDescription>
                            Select a destination folder for {selectedItems.size} file(s)
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label>Destination Folder</Label>
                            <Select
                                value={moveTargetFolder || 'root'}
                                onValueChange={(value) => setMoveTargetFolder(value === 'root' ? null : value)}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Select folder" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="root">Root (File Library)</SelectItem>
                                    {allFolders.map(folder => (
                                        <SelectItem key={folder.id} value={folder.id}>
                                            {folder.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setMoveDialogOpen(false);
                                setMoveTargetFolder(null);
                            }}
                        >
                            Cancel
                        </Button>
                        <Button onClick={handleMove}>
                            Move
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete Items</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to delete {selectedItems.size} item(s)? This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
